import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SchemaProps {
    type?: 'WebPage' | 'AboutPage' | 'ContactPage' | 'Service' | 'FAQPage' | 'BlogPosting';
    name?: string;
    description?: string;
    url?: string;
    image?: string;
    datePublished?: string;
    dateModified?: string;
    author?: {
        name: string;
        type: 'Person' | 'Organization';
    };
    breadcrumbs?: {
        name: string;
        item: string;
    }[];
    faqs?: {
        question: string;
        answer: string;
    }[];
    reviews?: {
        author: string;
        rating: number;
        body: string;
        date: string;
    }[];
    serviceType?: string;
    areaServed?: string | {
        '@type'?: string;
        name?: string;
    };
    provider?: {
        '@type'?: string;
        name?: string;
        url?: string;
    };
    aggregateRating?: {
        ratingValue: number;
        reviewCount: number;
    };
}

export const Schema: React.FC<SchemaProps> = ({
    type = 'WebPage',
    name,
    description,
    url,
    image,
    datePublished,
    dateModified,
    author,
    breadcrumbs,
    faqs,
    reviews,
    serviceType,
    areaServed,
    provider,
    aggregateRating
}) => {
    const baseUrl = 'https://rotanabiz.com';
    const organizationId = `${baseUrl}/#organization`;
    const websiteId = `${baseUrl}/#website`;

    const organization = {
        '@type': 'Organization',
        '@id': organizationId,
        'name': 'RotanaBiz',
        'url': baseUrl,
        'logo': {
            '@type': 'ImageObject',
            'url': `${baseUrl}/rotanabiz-logo.webp`,
            'width': '600',
            'height': '165'
        },
        'sameAs': [
            'https://www.facebook.com/rotanabiz',
            'https://twitter.com/rotanabiz',
            'https://www.linkedin.com/company/rotanabiz',
            'https://www.instagram.com/rotanabiz'
        ],
        'contactPoint': {
            '@type': 'ContactPoint',
            'telephone': '+971-58-818-1824',
            'contactType': 'customer service',
            'areaServed': 'AE',
            'availableLanguage': ['en', 'ar']
        }
    };

    const website = {
        '@type': 'WebSite',
        '@id': websiteId,
        'url': baseUrl,
        'name': 'RotanaBiz',
        'description': 'UAE Business Setup & Corporate Services',
        'publisher': { '@id': organizationId }
    };

    const graph: any[] = [organization, website];

    // Current WebPage
    const webPage: any = {
        '@type': type === 'AboutPage' || type === 'ContactPage' ? type : 'WebPage',
        '@id': `${url || baseUrl}/#webpage`,
        'url': url || baseUrl,
        'name': name,
        'description': description,
        'isPartOf': { '@id': websiteId },
        'publisher': { '@id': organizationId },
        'inLanguage': 'en-AE'
    };

    if (datePublished) webPage.datePublished = datePublished;
    if (dateModified) webPage.dateModified = dateModified;

    graph.push(webPage);

    // Breadcrumbs
    if (breadcrumbs && breadcrumbs.length > 0) {
        graph.push({
            '@type': 'BreadcrumbList',
            '@id': `${url || baseUrl}/#breadcrumb`,
            'itemListElement': breadcrumbs.map((bc, index) => ({
                '@type': 'ListItem',
                'position': index + 1,
                'name': bc.name,
                'item': bc.item.startsWith('http') ? bc.item : `${baseUrl}${bc.item.startsWith('/') ? '' : '/'}${bc.item}`
            }))
        });
    }

    // Main Entity (Service, BlogPosting, etc.)
    if (type === 'Service') {
        // Google Review Snippets support Product or LocalBusiness, but not generic Service.
        // If we have reviews, we treat this service as a Product to get the rich snippet.
        const isProduct = (reviews && reviews.length > 0) || !!aggregateRating;
        const schemaType = isProduct ? 'Product' : 'Service';

        const service: any = {
            '@type': schemaType,
            'name': name,
            'description': description,
            'url': url,
            ...(image && { 'image': image }),
            // Product uses brand, Service uses provider
            ...(isProduct ? {
                'brand': {
                    '@type': 'Brand',
                    'name': 'RotanaBiz'
                }
            } : {
                'provider': provider || { '@id': organizationId },
                'serviceType': serviceType
            }),
            // Keep areaServed for both as it's useful context, even if Product doesn't strictly validate it in all schemas
            'areaServed': areaServed || {
                '@type': 'Country',
                'name': 'United Arab Emirates'
            }
        };

        if (aggregateRating) {
            service.aggregateRating = {
                '@type': 'AggregateRating',
                'ratingValue': Number(aggregateRating.ratingValue),
                'reviewCount': Number(aggregateRating.reviewCount),
                'bestRating': 5,
                'worstRating': 1
            };
        }

        if (reviews && reviews.length > 0) {
            service.review = reviews.map(r => ({
                '@type': 'Review',
                'author': { '@type': 'Person', 'name': r.author },
                'reviewRating': {
                    '@type': 'Rating',
                    'ratingValue': Number(r.rating),
                    'bestRating': 5,
                    'worstRating': 1
                },
                'reviewBody': r.body,
                'datePublished': r.date
            }));
        }

        graph.push(service);
    } else if (type === 'BlogPosting') {
        const blogPost: any = {
            '@type': 'BlogPosting',
            '@id': `${url}/#main-content`,
            'headline': name,
            'description': description,
            'image': image || `${baseUrl}/rotanabiz-logo.webp`,
            'datePublished': datePublished,
            'dateModified': dateModified || datePublished,
            'mainEntityOfPage': { '@id': `${url}/#webpage` },
            'publisher': { '@id': organizationId },
            'author': author ? {
                '@type': author.type,
                'name': author.name,
                ...(author.type === 'Organization' && { 'url': `${baseUrl}/about` })
            } : { '@id': organizationId }
        };
        graph.push(blogPost);
    } else if (type === 'ContactPage') {
        const localBusiness = {
            '@type': 'LocalBusiness',
            '@id': `${baseUrl}/#localbusiness`,
            'name': 'RotanaBiz - Rotana Business Center',
            'image': `${baseUrl}/images/rotana-office.webp`,
            'description': 'Premier business setup consultancy and corporate services provider in the UAE, helping entrepreneurs launch and grow their businesses since 2013.',
            'address': {
                '@type': 'PostalAddress',
                'streetAddress': 'Julphar, Al Mataf Road',
                'addressLocality': 'Ras Al Khaimah',
                'addressRegion': 'Ras Al Khaimah',
                'addressCountry': 'AE'
            },
            'geo': {
                '@type': 'GeoCoordinates',
                'latitude': '25.849529',
                'longitude': '56.00585'
            },
            'url': baseUrl,
            'telephone': '+971-7-2430438',
            'email': 'info@rotanabiz.com',
            'priceRange': '$$',
            'openingHoursSpecification': [
                {
                    '@type': 'OpeningHoursSpecification',
                    'dayOfWeek': ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
                    'opens': '09:00',
                    'closes': '18:00'
                },
                {
                    '@type': 'OpeningHoursSpecification',
                    'dayOfWeek': 'Saturday',
                    'opens': '09:00',
                    'closes': '14:00'
                }
            ],
            'sameAs': [
                'https://www.facebook.com/rotanabiz',
                'https://twitter.com/rotanabiz',
                'https://www.linkedin.com/company/rotanabiz',
                'https://www.instagram.com/rotanabiz'
            ],
            'contactPoint': [
                {
                    '@type': 'ContactPoint',
                    'telephone': '+971-7-2430438',
                    'contactType': 'customer service',
                    'areaServed': 'AE',
                    'availableLanguage': ['en', 'ar', 'hi', 'ur', 'tl', 'ru']
                },
                {
                    '@type': 'ContactPoint',
                    'telephone': '+971-58-818-1824',
                    'contactType': 'customer service',
                    'contactOption': 'TollFree',
                    'areaServed': 'AE',
                    'availableLanguage': ['en', 'ar', 'hi', 'ur', 'tl', 'ru']
                }
            ]
        };

        if (aggregateRating) {
            (localBusiness as any).aggregateRating = {
                '@type': 'AggregateRating',
                'ratingValue': aggregateRating.ratingValue,
                'reviewCount': aggregateRating.reviewCount,
                'bestRating': '5',
                'worstRating': '1'
            };
        }

        graph.push(localBusiness);
    }

    // FAQPage
    if (faqs && faqs.length > 0) {
        graph.push({
            '@type': 'FAQPage',
            '@id': `${url || baseUrl}/#faq`,
            'mainEntity': faqs.map(faq => ({
                '@type': 'Question',
                'name': faq.question,
                'acceptedAnswer': {
                    '@type': 'Answer',
                    'text': faq.answer
                }
            }))
        });
    }

    return (
        <Helmet>
            <script type="application/ld+json">
                {JSON.stringify({
                    '@context': 'https://schema.org',
                    '@graph': graph
                })}
            </script>
        </Helmet>
    );
};
